/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import { task } from 'ember-concurrency';
import paginate from 'cio/utils/paginate';

import AlertsService from 'cio/services/alerts';
import ArrayProxy from '@ember/array/proxy';
import CategoryModel from 'cio/models/category';
import DS from 'ember-data';
import SavedRowModel from 'cio/models/saved-row';
import { sortByCaseInsensitive, SortDirection } from 'cio/utils/sort';
import { SavedRowProxy } from 'cio/components/manage-saved-rows-modal/saved-rows-table/edit-form';

const ROWS_PER_PAGE = 10;
const MAX_SAVED_ROWS_REQUEST_ATTEMPTS = 3;

export enum SortProperty {
  Name = 'name',
  Category = 'category.name',
}

interface ManageSavedRowsModalArgs {
  categories?: ArrayProxy<CategoryModel>;
  disabled: boolean;
  onClose: () => unknown;
}

export default class ManageSavedRowsModal extends Component<ManageSavedRowsModalArgs> {
  @service alerts!: AlertsService;
  @service store!: DS.Store;

  @tracked page = 1;
  @tracked rowPendingDeletion?: SavedRowModel;
  @tracked rowPendingEdit?: SavedRowModel;
  @tracked savedRowsFetchAttempts = 0;
  @tracked searchTerm = '';
  @tracked sortAsc = false;
  @tracked selectedCategoryFilterValue = 'all';
  @tracked sortBy: SortProperty = SortProperty.Name;

  noRowsSavedImage = new URL('cio/assets/images/illustrations/no-rows-saved.png', import.meta.url).toString();
  emptySearchImage = new URL('cio/assets/images/illustrations/empty-search.png', import.meta.url).toString();

  readonly perPage = ROWS_PER_PAGE;

  get isLoading() {
    return this.fetchSavedRowsTask.isRunning;
  }
  get categoryFilterOptions() {
    if (isEmpty(this.args.categories)) {
      return [];
    }

    return [
      {
        label: 'All categories',
        value: 'all',
      },
      {
        label: 'Uncategorized',
        value: 'uncategorized',
      },
      ...this.args.categories!.map((category) => ({ label: category.name, value: category.id })),
    ];
  }
  get savedRows() {
    return this.fetchSavedRowsTask.lastSuccessful?.value ?? [];
  }
  get hasSearchTerm() {
    return isPresent(this.searchTerm);
  }
  get hasCategoryFilter() {
    return this.selectedCategoryFilterValue !== 'all';
  }
  get hasActiveFilters() {
    return this.hasSearchTerm || this.hasCategoryFilter;
  }
  get filteredSavedRows() {
    if (!this.hasActiveFilters) {
      return this.savedRows;
    }

    return (this.savedRows as ArrayProxy<SavedRowModel> | any[]).filter((row) => {
      let matchesSearchTerm = !this.hasSearchTerm
        ? true
        : row.name.toLowerCase().includes(this.searchTerm.toLowerCase());
      let matchesCategoryFilter = true;
      let rowCategoryId = row.category?.get('id')?.toString();

      if (this.hasCategoryFilter) {
        if (this.selectedCategoryFilterValue === 'uncategorized') {
          matchesCategoryFilter = isEmpty(rowCategoryId);
        } else {
          matchesCategoryFilter = rowCategoryId === this.selectedCategoryFilterValue;
        }
      }

      return matchesSearchTerm && matchesCategoryFilter;
    });
  }
  get sortDirection() {
    return this.sortAsc ? 'asc' : 'desc';
  }
  get sortedSavedRows() {
    let sorted = sortByCaseInsensitive(this.filteredSavedRows as any[], [this.sortBy]);
    return this.sortAsc ? sorted.reverse() : sorted;
  }
  get paginatedSavedRows() {
    return paginate(this.sortedSavedRows, {
      page: this.page - 1,
      perPage: this.perPage,
    });
  }
  get hasPendingInteraction() {
    return isPresent(this.rowPendingDeletion) || isPresent(this.rowPendingEdit);
  }

  deleteSavedRowTask = task(async (row: SavedRowModel) => {
    await row.destroyRecord();
    this.rowPendingDeletion = undefined;
  });

  updateSavedRowTask = task(async ({ name, category }: SavedRowProxy) => {
    if (this.rowPendingEdit) {
      try {
        this.rowPendingEdit.name = name;
        this.rowPendingEdit.category = category;
        this.rowPendingEdit.data.metadata = {
          ...this.rowPendingEdit.data.metadata,
          name,
          category_id: category?.get('id'),
        };

        await this.rowPendingEdit.save();
        this.rowPendingEdit = undefined;
      } catch {
        // handled by validations
      }
    }
  });

  fetchSavedRowsTask = task(async () => {
    try {
      return this.store.findAll('saved-row');
    } catch {
      // try to load the saved row records up to 3 times
      if (this.savedRowsFetchAttempts < MAX_SAVED_ROWS_REQUEST_ATTEMPTS) {
        this.fetchSavedRowsTask.perform();
        this.savedRowsFetchAttempts = this.savedRowsFetchAttempts + 1;
      } else {
        this.args.onClose();
        this.alerts.error(
          'saved_rows.request.failure',
          'Unable to load Saved Rows. Please try again or contact support for help.',
        );
      }

      return undefined;
    }
  });

  @action
  setSearchTerm(q: string) {
    this.searchTerm = q;
    this.page = 1;
  }
  @action
  setSelectedCategoryFilterValue({ value }: { value: string }) {
    this.selectedCategoryFilterValue = value;
    this.page = 1;
  }
  @action
  setSort(sortBy: SortProperty, sortDirection: SortDirection) {
    this.sortBy = sortBy;
    this.sortAsc = sortDirection === SortDirection.Ascending;
    this.page = 1;
  }
}
